import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ArrowRight,
  Ban,
  CheckCircle2,
  Clock,
  DollarSign,
  Eye,
  FileText,
  Lock,
  LockOpen,
  Plus,
  Send,
  XCircle,
} from 'lucide-react';
import { getFinanceStatusLabel } from '@/lib/work-items/financeStatusConfig';

const STATUS_CONFIG: Record<string, {
  lightBg: string;
  lightText: string;
  darkBg: string;
  darkText: string;
  icon: LucideIcon;
}> = {
  draft: { lightBg: 'bg-gray-100', lightText: 'text-gray-700', darkBg: 'bg-gray-700/80', darkText: 'text-gray-100', icon: FileText },
  pending: { lightBg: 'bg-yellow-100', lightText: 'text-yellow-700', darkBg: 'bg-yellow-900/50', darkText: 'text-yellow-200', icon: Clock },
  pending_approval: { lightBg: 'bg-yellow-100', lightText: 'text-yellow-700', darkBg: 'bg-yellow-900/50', darkText: 'text-yellow-200', icon: Clock },
  sent: { lightBg: 'bg-blue-100', lightText: 'text-blue-700', darkBg: 'bg-blue-900/50', darkText: 'text-blue-200', icon: Send },
  viewed: { lightBg: 'bg-indigo-100', lightText: 'text-indigo-700', darkBg: 'bg-indigo-900/50', darkText: 'text-indigo-200', icon: Eye },
  paid: { lightBg: 'bg-green-100', lightText: 'text-green-700', darkBg: 'bg-green-900/50', darkText: 'text-green-200', icon: CheckCircle2 },
  partially_paid: { lightBg: 'bg-amber-100', lightText: 'text-amber-700', darkBg: 'bg-amber-900/50', darkText: 'text-amber-200', icon: DollarSign },
  overdue: { lightBg: 'bg-red-100', lightText: 'text-red-700', darkBg: 'bg-red-900/50', darkText: 'text-red-200', icon: AlertCircle },
  void: { lightBg: 'bg-gray-200', lightText: 'text-gray-500', darkBg: 'bg-gray-700/80', darkText: 'text-gray-300', icon: XCircle },
  cancelled: { lightBg: 'bg-gray-200', lightText: 'text-gray-500', darkBg: 'bg-gray-700/80', darkText: 'text-gray-300', icon: Ban },
  accepted: { lightBg: 'bg-green-100', lightText: 'text-green-700', darkBg: 'bg-green-900/50', darkText: 'text-green-200', icon: CheckCircle2 },
  declined: { lightBg: 'bg-red-100', lightText: 'text-red-700', darkBg: 'bg-red-900/50', darkText: 'text-red-200', icon: XCircle },
  expired: { lightBg: 'bg-orange-100', lightText: 'text-orange-700', darkBg: 'bg-orange-900/50', darkText: 'text-orange-200', icon: Clock },
  converted: { lightBg: 'bg-purple-100', lightText: 'text-purple-700', darkBg: 'bg-purple-900/50', darkText: 'text-purple-200', icon: ArrowRight },
  applied: { lightBg: 'bg-green-100', lightText: 'text-green-700', darkBg: 'bg-green-900/50', darkText: 'text-green-200', icon: CheckCircle2 },
};

export function FinanceStatusBadge({
  status,
  isDark = false,
  itemType,
}: {
  status: string;
  isDark?: boolean;
  itemType?: string;
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = config.icon;
  // Try SSOT label first, fall back to manual formatting
  const ssotLabel = itemType ? getFinanceStatusLabel(status, itemType) : null;
  const displayStatus = ssotLabel && ssotLabel !== status
    ? ssotLabel
    : status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${isDark ? `${config.darkBg} ${config.darkText}` : `${config.lightBg} ${config.lightText}`}`}>
      <Icon size={14} />
      {displayStatus}
    </span>
  );
}

export type FinanceSummaryTone = 'default' | 'positive' | 'danger' | 'warning' | 'muted';

export interface FinanceSummaryRow {
  label: ReactNode;
  value: ReactNode;
  tone?: FinanceSummaryTone;
}

export function FinanceSummaryCard({
  isDarkMode,
  title,
  rows,
  totalLabel = 'Total',
  totalValue,
  totalTone = 'positive',
  footerRows = [],
  compact = false,
  // Total lock mode
  totalLocked = false,
  onToggleLock,
  hasDiscrepancy = false,
  onAddBalancingLine,
}: {
  isDarkMode: boolean;
  title: ReactNode;
  rows: FinanceSummaryRow[];
  totalLabel?: ReactNode;
  totalValue: ReactNode;
  totalTone?: FinanceSummaryTone;
  footerRows?: FinanceSummaryRow[];
  compact?: boolean;
  // Total lock mode
  totalLocked?: boolean;
  onToggleLock?: () => void;
  hasDiscrepancy?: boolean;
  onAddBalancingLine?: () => void;
}) {
  const secondaryTextClass = isDarkMode ? 'text-slate-300' : 'text-slate-400';
  const mutedTextClass = 'text-slate-500';
  const toneClasses: Record<FinanceSummaryTone, string> = {
    default: 'text-white',
    positive: 'text-green-400',
    danger: 'text-red-400',
    warning: 'text-orange-400',
    muted: 'text-slate-400',
  };

  return (
    <div className={`rounded-lg ${compact ? 'p-3' : 'p-4'} ${
      isDarkMode
        ? 'bg-slate-900 text-white border border-slate-700'
        : 'bg-slate-800 text-white'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-xs font-semibold uppercase tracking-wide ${secondaryTextClass}`}>
          {title}
        </h3>
        {onToggleLock && (
          <button
            type="button"
            onClick={onToggleLock}
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
              totalLocked
                ? 'bg-blue-500/20 border-blue-400/40 text-blue-300'
                : isDarkMode
                  ? 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700'
                  : 'bg-slate-700 border-slate-600 text-slate-400 hover:bg-slate-600'
            }`}
            title={totalLocked
              ? 'Total locked to OCR slip amount — line items must reconcile to this. Click to unlock and recompute from line items.'
              : 'Total computed from line items. Click to lock to the OCR slip amount.'}
          >
            {totalLocked ? <Lock size={10} /> : <LockOpen size={10} />}
            {totalLocked ? 'Locked' : 'Unlocked'}
          </button>
        )}
      </div>

      <div className="space-y-2 text-sm">
        {rows.map((row, index) => (
          <div key={`summary-row-${index}`} className={`flex justify-between ${row.tone ? toneClasses[row.tone] : ''}`}>
            <span className={row.tone ? undefined : secondaryTextClass}>{row.label}</span>
            <span className={`font-mono ${row.tone ? toneClasses[row.tone] : ''}`}>{row.value}</span>
          </div>
        ))}

        <div className={`border-t pt-2 mt-2 ${isDarkMode ? 'border-slate-600' : 'border-slate-700'}`}>
          <div className="flex justify-between text-lg font-semibold">
            <span className="inline-flex items-center gap-1.5">
              {totalLabel}
              {totalLocked && <Lock size={12} className="text-blue-400" />}
            </span>
            <span className={`font-mono ${toneClasses[totalTone]}`}>{totalValue}</span>
          </div>
        </div>

        {/* Discrepancy warning + balancing line button */}
        {totalLocked && hasDiscrepancy && onAddBalancingLine && (
          <div className={`mt-2 rounded-md p-2 ${isDarkMode ? 'bg-red-900/30 border border-red-700/50' : 'bg-red-500/15 border border-red-400/30'}`}>
            <div className="flex items-center gap-1.5 text-red-400 text-xs font-medium">
              <AlertCircle size={12} />
              <span>Line items don&apos;t match slip total</span>
            </div>
            <button
              type="button"
              onClick={onAddBalancingLine}
              className="mt-1.5 w-full inline-flex items-center justify-center gap-1 text-[11px] py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-400/40 transition-colors"
            >
              <Plus size={11} />
              Add balancing line
            </button>
          </div>
        )}

        {footerRows.map((row, index) => (
          <div key={`summary-footer-row-${index}`} className="flex justify-between text-xs">
            <span className={row.tone === 'muted' ? mutedTextClass : secondaryTextClass}>{row.label}</span>
            <span className={`font-mono ${row.tone ? toneClasses[row.tone] : 'text-slate-400'}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface FinanceTabOption<T extends string = string> {
  id: T;
  label: string;
  icon: LucideIcon;
}

export function FinanceDocumentTabBar<T extends string>({
  tabs,
  activeTab,
  onChange,
  isDarkMode,
  compact = false,
}: {
  tabs: Array<FinanceTabOption<T>>;
  activeTab: T;
  onChange: (tabId: T) => void;
  isDarkMode: boolean;
  compact?: boolean;
}) {
  return (
    <div className="flex gap-1 -mb-px">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`font-medium border-b-2 flex items-center gap-2 transition-colors ${compact ? 'flex-1 justify-center px-2 py-2.5 text-xs' : 'px-4 py-3 text-sm'} ${
              isActive
                ? isDarkMode
                  ? 'text-blue-400 border-blue-400'
                  : 'text-blue-600 border-blue-600'
                : isDarkMode
                  ? 'text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon size={16} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
